import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Play, Square, Download, Share2, X } from "lucide-react";
import _logoSrc      from "../assets/logo.png";
import _logoTextSrc  from "../assets/logo_text.png";
import _hanamaruSrc  from "../assets/gif/hanamaru.mp4";

// ─── i18n ────────────────────────────────────────────────────────────────────
const I18N = {
  EN: {
    searchPlaceholder: "Search characters…",
    hiragana:     "Hiragana",
    katakana:     "Katakana",
    play:         "Play",
    replay:       "Replay",
    playing:      "Playing…",
    playAll:      "Play All",
    practiceAll:  "Practice All",
    practiceAgain:"Practice Again",
    stop:         "Stop",
    resume:       "Resume",
    selectChar:   "Select a character above",
    loading:      "Loading…",
    noMatch:      "No characters match.",
    practiceHint: (n) => { const s = n===1?"1st":n===2?"2nd":n===3?"3rd":`${n}th`; return `${s} stroke`; },
    practiceWin:  "Perfect! ✓",
    congratsTitle:  (type, n) => `Congrats! You wrote all ${n} ${type} characters!`,
    congratsSub:    "Save this for your memory!",
    charactersDone: (n) => `${n} Characters done!`,
    save:           "Save Image",
    share:          "Share",
  },
  ZH: {
    searchPlaceholder: "搜索字母…",
    hiragana:     "平假名",
    katakana:     "片假名",
    play:         "播放",
    replay:       "重播",
    playing:      "播放中…",
    playAll:      "播放全部",
    practiceAll:  "全部练习",
    practiceAgain:"再练一次",
    stop:         "停止",
    resume:       "继续",
    selectChar:   "先选择字母",
    loading:      "加载中…",
    noMatch:      "没有符合的字符。",
    practiceHint: (n) => { const s = n===1?"一":n===2?"二":n===3?"三":n===4?"四":String(n); return `第${s}笔`; },
    practiceWin:  "写得很好！✓",
    congratsTitle:  (type, n) => `恭喜！你完成了全部${n}个${type}字母！`,
    congratsSub:    "保存下来作为纪念吧！",
    charactersDone: (n) => `你已经写完了${n}个字！`,
    save:           "保存图片",
    share:          "分享",
  },
  JP: {
    searchPlaceholder: "もじをさがす",
    hiragana:     "ひらがな",
    katakana:     "カタカナ",
    play:         "みる",
    replay:       "もういっかいみる",
    playing:      "よくみてね…",
    playAll:      "ぜんぶみる",
    practiceAll:  "ぜんぶかく",
    practiceAgain:"もういっかいかく",
    stop:         "とめる",
    resume:       "つづける",
    selectChar:   "うえのもじをえらんでください",
    loading:      "よみこみちゅう",
    noMatch:      "みつけられませんでした",
    practiceHint: (n) => { const s = n===1?"いっ":n===2?"に":n===3?"さん":n===4?"よん":String(n); return `${s}かくめ`; },
    practiceWin:  "かんぺき！✓",
    congratsTitle:  (type, n) => `すごい！${type}${n}もじをぜんぶかいたよ！`,
    congratsSub:    "がんばったきろくをあとでみかえそう！",
    charactersDone: (n) => `${n}もじかいたよ！`,
    save:           "がぞうをほぞん",
    share:          "シェア",
  },
};

// ─── Web Audio beep (fallback when audio files are missing) ──────────────────
function playBeep(freq = 880, dur = 0.12, vol = 0.25) {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.type = "sine"; osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + dur);
    osc.start(); osc.stop(ctx.currentTime + dur);
    setTimeout(() => ctx.close(), (dur + 0.1) * 1000);
  } catch (_) {}
}

// ─── Canvas helpers (module-level) ───────────────────────────────────────────
function getCanvasPos(e, canvas) {
  if (!canvas) return { x: 0, y: 0 };
  const rect = canvas.getBoundingClientRect();
  const sx   = CANVAS_SIZE / rect.width;
  const sy   = CANVAS_SIZE / rect.height;
  return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
}

function projectOnStroke(pos, stroke, minPct = 0) {
  const pts      = stroke.pts;
  // const startIdx = Math.max(0, Math.floor(minPct * (pts.length - 1)));
  const BACKTRACK = 8; // try 5-10

const startIdx = Math.max(
  0,
  Math.floor(minPct * (pts.length - 1)) - BACKTRACK
);
  let minDist    = Infinity;
  let bestIdx    = startIdx;
  // Scan all forward points — no early exit so curved strokes map correctly
  for (let i = startIdx; i < pts.length; i++) {
    const dist = Math.hypot(pts[i].x - pos.x, pts[i].y - pos.y);
    if (dist < minDist) { minDist = dist; bestIdx = i; }
  }
  // return { pct: bestIdx / Math.max(pts.length - 1, 1), dist: minDist };
  const pct = bestIdx / Math.max(pts.length - 1, 1);

return {
    pct: Math.max(minPct, pct),
    dist: minDist
};
}


// ─── Constants ────────────────────────────────────────────────────────────────
const CANVAS_SIZE        = 2160;
const PINK_DOT_RADIUS    = 68;
const PINK_COLOR         = "#fe5596";
const STROKE_DRAW_COLOR  = "#fe5596";
const STROKE_DONE_COLOR  = "#000000";
const STROKE_GRAY_COLOR  = "#ffffff";
const PRACTICE_BLUE      = "#2797c4";
const PRACTICE_DOT_SCALE = 1.0;    // normal dot size
const PRACTICE_TAP_SCALE = 2.0;    // touch tap area for start dot
const PRACTICE_MOVE_TOL  = 0.8;    // max distance (× lineWidth) for move to register — tight so crossing strokes (あ、め) don't misfire
const DEBUG_PRACTICE     = true;   // show tap area for testing
const PRACTICE_THRESHOLD = 0.80;   // require 80% of stroke covered to accept
const PINK_DOT_DURATION  = 300;
const PAUSE_DURATION     = 0;
const END_PAUSE          = 150;
const BASE_PX_MS         = 1.0;
const STROKE_SOUND_URL   = "/audio/stroke-sound.mp4";

// ─── Asset maps ───────────────────────────────────────────────────────────────
const SVG_URL_MAP    = {};
const SVG_ALL_MAP    = {};
const CHAR_AUDIO_MAP = {};

try {
  const req = require.context("../assets/svg", false, /\.svg$/i);
  req.keys().forEach((k) => {
    const mAll = k.match(/[/\\](hiragana|katakana)_all\.svg$/i);
    if (mAll) { SVG_ALL_MAP[mAll[1].toLowerCase()] = req(k); return; }
    const m = k.match(/[/\\](hiragana|katakana)_([a-z]+)\.svg$/i);
    if (m) SVG_URL_MAP[`${m[1].toLowerCase()}_${m[2].toLowerCase()}`] = req(k);
  });
} catch (_) {}

try {
  const req = require.context("../assets/audio", false, /\.(mp3|wav|ogg|m4a)$/i);
  req.keys().forEach((k) => {
    if (/[/\\](hiragana|katakana)_/i.test(k)) return;
    // Explicit matching for feedback sounds (handles any filename containing the word)
    const mFb = k.match(/[/\\](correct|wrong|tap)\b[^/\\]*\.\w+$/i);
    if (mFb) {
      const key = mFb[1].toLowerCase();
      if (!CHAR_AUDIO_MAP[key] || k.toLowerCase().endsWith(".m4a"))
        CHAR_AUDIO_MAP[key] = req(k);
      return;
    }
    // Generic character audio (romaji key like a, ka, shi…)
    const mPlain = k.match(/[/\\]([a-z]+)\.\w+$/i);
    if (mPlain) {
      const key = mPlain[1].toLowerCase();
      if (!CHAR_AUDIO_MAP[key] || k.toLowerCase().endsWith(".m4a"))
        CHAR_AUDIO_MAP[key] = req(k);
    }
  });
} catch (_) {}

// Goodjob audio (keyed by language: en / zh / jp)
const GOODJOB_AUDIO = { en: null, zh: null, jp: null };
try {
  const req = require.context("../assets/audio", false, /\.(mp3|wav|ogg|m4a)$/i);
  req.keys().forEach((k) => {
    const m = k.match(/[/\\]goodjob_(en|zh|jp)\.\w+$/i);
    if (m) GOODJOB_AUDIO[m[1].toLowerCase()] = req(k);
    else if (/[/\\]goodjob\.\w+$/i.test(k) && !GOODJOB_AUDIO.en)
      GOODJOB_AUDIO.en = req(k);
  });
} catch (_) {}

function getSVGSource(c) {
  const prefix = c.groups.includes("katakana") ? "katakana" : "hiragana";
  const perChar = SVG_URL_MAP[`${prefix}_${c.key}`];
  if (perChar) return { url: perChar, charKey: null };
  const all = SVG_ALL_MAP[prefix];
  if (all)    return { url: all, charKey: c.key };
  return null;
}

// ─── SVG parsing ─────────────────────────────────────────────────────────────
function parseSVGStrokes(svgText, charKey = null) {
  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;visibility:hidden;";
  container.innerHTML = svgText;
  document.body.appendChild(container);

  const strokes = [];
  try {
    const svgEl = container.querySelector("svg");
    if (!svgEl) return [];

    const vb = (svgEl.getAttribute("viewBox") || `0 0 ${CANVAS_SIZE} ${CANVAS_SIZE}`)
      .split(/[\s,]+/).map(Number);
    const scaleX = CANVAS_SIZE / (vb[2] || CANVAS_SIZE);
    const scaleY = CANVAS_SIZE / (vb[3] || CANVAS_SIZE);

    let defaultLW = 130;
    const styleText = svgEl.querySelector("style")?.textContent || "";
    const swM = styleText.match(/stroke-width\s*:\s*([\d.]+)/);
    if (swM) defaultLW = parseFloat(swM[1]) * ((scaleX + scaleY) / 2);

    const searchRoot = charKey
      ? (svgEl.querySelector(`g[id="${charKey}"]`) ||
         svgEl.querySelector(`g[id="${charKey.replace(/_/g, "_x5F_")}"]`) ||
         svgEl.querySelector(`g[id^="${charKey}_"]`))
      : svgEl;
    if (!searchRoot) return [];

    const overrideStyle = document.createElementNS("http://www.w3.org/2000/svg", "style");
    overrideStyle.textContent = "* { display: block !important; }";
    svgEl.appendChild(overrideStyle);

    searchRoot.querySelectorAll("g[id^='stroke']").forEach((g) => {
      const strokeNum = parseInt(g.id.replace(/^stroke_(?:x5F_)?/i, ""), 10);
      const shape = g.querySelector("path, polyline, line, polygon");
      if (!shape) return;
      try {
        const totalLen = shape.getTotalLength();
        if (totalLen === 0) return;
        const SAMPLES = 150;
        let pts = [];
        for (let i = 0; i <= SAMPLES; i++) {
          const pt = shape.getPointAtLength((i / SAMPLES) * totalLen);
          pts.push({ x: pt.x * scaleX, y: pt.y * scaleY });
        }
        // Detect closed-loop paths (e.g. オ stroke 1: M→tip→M z).
        // These store both the outward AND return trip, so pts[0]≈pts[last].
        // Keep only the first half (outward direction) so the direction check
        // and progress threshold work correctly.
        const loopDist = Math.hypot(pts[0].x - pts[pts.length-1].x,
                                     pts[0].y - pts[pts.length-1].y);
        if (loopDist < 50) {
          pts = pts.slice(0, Math.ceil(pts.length / 2) + 1);
        }
        let length = 0;
        for (let i = 1; i < pts.length; i++) {
          const dx = pts[i].x - pts[i-1].x, dy = pts[i].y - pts[i-1].y;
          length += Math.sqrt(dx*dx + dy*dy);
        }
        strokes.push({ strokeNum, pts, lineWidth: defaultLW, length });
      } catch (err) { console.warn("getTotalLength failed:", g.id, err); }
    });

    strokes.sort((a, b) => a.strokeNum - b.strokeNum);
    svgEl.removeChild(overrideStyle);
  } finally {
    document.body.removeChild(container);
  }
  return strokes;
}

const _strokeCache = {};
async function loadSVGStrokes(url, charKey = null) {
  const cacheKey = `${url}::${charKey}`;
  if (_strokeCache[cacheKey]) return _strokeCache[cacheKey];
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to load SVG: ${resp.status}`);
  const text    = await resp.text();
  const strokes = parseSVGStrokes(text, charKey);
  _strokeCache[cacheKey] = strokes;
  return strokes;
}

// ─── Canvas helpers ───────────────────────────────────────────────────────────
function clearCanvas(ctx) {
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
}

// lwOverride: optional lineWidth override (for thin practice lines)
function drawStroke(ctx, stroke, progress = 1, color = STROKE_DONE_COLOR, lwOverride = null) {
  const { pts, lineWidth, length: totalLen } = stroke;
  if (!pts || pts.length === 0 || !totalLen) return;
  const target = totalLen * Math.min(Math.max(progress, 0), 1);

  ctx.save();
  ctx.strokeStyle = color; ctx.lineWidth = lwOverride ?? (lineWidth || 130);
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);

  let drawn = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i-1].x, dy = pts[i].y - pts[i-1].y;
    const segLen = Math.sqrt(dx*dx + dy*dy);
    if (drawn + segLen >= target) {
      const t = segLen === 0 ? 0 : (target - drawn) / segLen;
      ctx.lineTo(pts[i-1].x + dx*t, pts[i-1].y + dy*t);
      break;
    }
    ctx.lineTo(pts[i].x, pts[i].y); drawn += segLen;
  }
  ctx.stroke(); ctx.restore();
}

function drawPinkDot(ctx, pt) {
  if (!pt) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(pt.x * CANVAS_SIZE, pt.y * CANVAS_SIZE, PINK_DOT_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = PINK_COLOR; ctx.fill(); ctx.restore();
}

function drawStrokeIndicator(ctx, stroke, strokeNum) {
  const { pts } = stroke;
  if (!pts || pts.length < 2) return;

  const LOOK_DIST = 180;
  let accumulated = 0, dirIdx = pts.length - 1;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i-1].x, dy = pts[i].y - pts[i-1].y;
    accumulated += Math.sqrt(dx*dx + dy*dy);
    if (accumulated >= LOOK_DIST) { dirIdx = i; break; }
  }
  const rawDx = pts[dirIdx].x - pts[0].x, rawDy = pts[dirIdx].y - pts[0].y;
  const d   = Math.sqrt(rawDx*rawDx + rawDy*rawDy) || 1;
  const cos = rawDx / d, sin = rawDy / d;
  const ox  = pts[0].x, oy = pts[0].y;

  const GAP = 100, SHAFT = 150, HEAD_LEN = 75, HEAD_W = 38;
  const LINE_W = 20, BORDER_W = 10, FONT_SZ = 120, MARGIN = 100;
  const BLUE = "#2797c4";

  let tipX  = ox - cos * GAP, tipY  = oy - sin * GAP;
  let tailX = tipX - cos * SHAFT, tailY = tipY - sin * SHAFT;

  const sx = tailX < MARGIN ? MARGIN - tailX : tailX > CANVAS_SIZE - MARGIN ? CANVAS_SIZE - MARGIN - tailX : 0;
  const sy = tailY < MARGIN ? MARGIN - tailY : tailY > CANVAS_SIZE - MARGIN ? CANVAS_SIZE - MARGIN - tailY : 0;
  tipX += sx; tipY += sy; tailX += sx; tailY += sy;

  const adx = tipX - tailX, ady = tipY - tailY;
  const al  = Math.sqrt(adx*adx + ady*ady) || 1;
  const ac  = adx / al, as_ = ady / al;

  const pX = -sin, pY = cos, NOFF = FONT_SZ * 0.9;
  const score = (x, y) => Math.min(x, CANVAS_SIZE - x, y, CANVAS_SIZE - y);
  const n1x = tailX + pX * NOFF, n1y = tailY + pY * NOFF;
  const n2x = tailX - pX * NOFF, n2y = tailY - pY * NOFF;
  const numX = score(n1x, n1y) >= score(n2x, n2y) ? n1x : n2x;
  const numY = score(n1x, n1y) >= score(n2x, n2y) ? n1y : n2y;

  const shaftEndX = tipX - ac * HEAD_LEN, shaftEndY = tipY - as_ * HEAD_LEN;
  const b1x = shaftEndX + (-as_) * HEAD_W, b1y = shaftEndY + ac  * HEAD_W;
  const b2x = shaftEndX +   as_  * HEAD_W, b2y = shaftEndY + (-ac) * HEAD_W;

  ctx.save();
  ctx.font = `bold ${FONT_SZ}px system-ui, sans-serif`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";

  ctx.strokeStyle = "white"; ctx.lineWidth = LINE_W + BORDER_W * 2;
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(tailX, tailY); ctx.lineTo(shaftEndX, shaftEndY); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(tipX, tipY); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y);
  ctx.closePath(); ctx.fillStyle = "white"; ctx.lineWidth = BORDER_W * 2; ctx.stroke(); ctx.fill();
  ctx.lineWidth = BORDER_W * 2; ctx.strokeStyle = "white"; ctx.strokeText(String(strokeNum), numX, numY);

  ctx.strokeStyle = BLUE; ctx.lineWidth = LINE_W; ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.beginPath(); ctx.moveTo(tailX, tailY); ctx.lineTo(shaftEndX, shaftEndY); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(tipX, tipY); ctx.lineTo(b1x, b1y); ctx.lineTo(b2x, b2y);
  ctx.closePath(); ctx.fillStyle = BLUE; ctx.fill();
  ctx.fillStyle = BLUE; ctx.fillText(String(strokeNum), numX, numY);
  ctx.restore();
}

// ─── Character data ───────────────────────────────────────────────────────────
const CHARS = [
  { key:"a",   char:"あ", romaji:"a",     num:1,  gridCol:10, gridRow:1, groups:["hiragana"] },
  { key:"i",   char:"い", romaji:"i",     num:2,  gridCol:10, gridRow:2, groups:["hiragana"] },
  { key:"u",   char:"う", romaji:"u",     num:3,  gridCol:10, gridRow:3, groups:["hiragana"] },
  { key:"e",   char:"え", romaji:"e",     num:4,  gridCol:10, gridRow:4, groups:["hiragana"] },
  { key:"o",   char:"お", romaji:"o",     num:5,  gridCol:10, gridRow:5, groups:["hiragana"] },
  { key:"ka",  char:"か", romaji:"ka",    num:6,  gridCol:9,  gridRow:1, groups:["hiragana"] },
  { key:"ki",  char:"き", romaji:"ki",    num:7,  gridCol:9,  gridRow:2, groups:["hiragana"] },
  { key:"ku",  char:"く", romaji:"ku",    num:8,  gridCol:9,  gridRow:3, groups:["hiragana"] },
  { key:"ke",  char:"け", romaji:"ke",    num:9,  gridCol:9,  gridRow:4, groups:["hiragana"] },
  { key:"ko",  char:"こ", romaji:"ko",    num:10, gridCol:9,  gridRow:5, groups:["hiragana"] },
  { key:"sa",  char:"さ", romaji:"sa",    num:11, gridCol:8,  gridRow:1, groups:["hiragana"] },
  { key:"shi", char:"し", romaji:"shi",   num:12, gridCol:8,  gridRow:2, groups:["hiragana"] },
  { key:"su",  char:"す", romaji:"su",    num:13, gridCol:8,  gridRow:3, groups:["hiragana"] },
  { key:"se",  char:"せ", romaji:"se",    num:14, gridCol:8,  gridRow:4, groups:["hiragana"] },
  { key:"so",  char:"そ", romaji:"so",    num:15, gridCol:8,  gridRow:5, groups:["hiragana"] },
  { key:"ta",  char:"た", romaji:"ta",    num:16, gridCol:7,  gridRow:1, groups:["hiragana"] },
  { key:"chi", char:"ち", romaji:"chi",   num:17, gridCol:7,  gridRow:2, groups:["hiragana"] },
  { key:"tsu", char:"つ", romaji:"tsu",   num:18, gridCol:7,  gridRow:3, groups:["hiragana"] },
  { key:"te",  char:"て", romaji:"te",    num:19, gridCol:7,  gridRow:4, groups:["hiragana"] },
  { key:"to",  char:"と", romaji:"to",    num:20, gridCol:7,  gridRow:5, groups:["hiragana"] },
  { key:"na",  char:"な", romaji:"na",    num:21, gridCol:6,  gridRow:1, groups:["hiragana"] },
  { key:"ni",  char:"に", romaji:"ni",    num:22, gridCol:6,  gridRow:2, groups:["hiragana"] },
  { key:"nu",  char:"ぬ", romaji:"nu",    num:23, gridCol:6,  gridRow:3, groups:["hiragana"] },
  { key:"ne",  char:"ね", romaji:"ne",    num:24, gridCol:6,  gridRow:4, groups:["hiragana"] },
  { key:"no",  char:"の", romaji:"no",    num:25, gridCol:6,  gridRow:5, groups:["hiragana"] },
  { key:"ha",  char:"は", romaji:"ha",    num:26, gridCol:5,  gridRow:1, groups:["hiragana"] },
  { key:"hi",  char:"ひ", romaji:"hi",    num:27, gridCol:5,  gridRow:2, groups:["hiragana"] },
  { key:"fu",  char:"ふ", romaji:"fu",    num:28, gridCol:5,  gridRow:3, groups:["hiragana"] },
  { key:"he",  char:"へ", romaji:"he",    num:29, gridCol:5,  gridRow:4, groups:["hiragana"] },
  { key:"ho",  char:"ほ", romaji:"ho",    num:30, gridCol:5,  gridRow:5, groups:["hiragana"] },
  { key:"ma",  char:"ま", romaji:"ma",    num:31, gridCol:4,  gridRow:1, groups:["hiragana"] },
  { key:"mi",  char:"み", romaji:"mi",    num:32, gridCol:4,  gridRow:2, groups:["hiragana"] },
  { key:"mu",  char:"む", romaji:"mu",    num:33, gridCol:4,  gridRow:3, groups:["hiragana"] },
  { key:"me",  char:"め", romaji:"me",    num:34, gridCol:4,  gridRow:4, groups:["hiragana"] },
  { key:"mo",  char:"も", romaji:"mo",    num:35, gridCol:4,  gridRow:5, groups:["hiragana"] },
  { key:"ya",  char:"や", romaji:"ya",    num:36, gridCol:3,  gridRow:1, groups:["hiragana"] },
  { key:"yu",  char:"ゆ", romaji:"yu",    num:37, gridCol:3,  gridRow:3, groups:["hiragana"] },
  { key:"yo",  char:"よ", romaji:"yo",    num:38, gridCol:3,  gridRow:5, groups:["hiragana"] },
  { key:"ra",  char:"ら", romaji:"ra",    num:39, gridCol:2,  gridRow:1, groups:["hiragana"] },
  { key:"ri",  char:"り", romaji:"ri",    num:40, gridCol:2,  gridRow:2, groups:["hiragana"] },
  { key:"ru",  char:"る", romaji:"ru",    num:41, gridCol:2,  gridRow:3, groups:["hiragana"] },
  { key:"re",  char:"れ", romaji:"re",    num:42, gridCol:2,  gridRow:4, groups:["hiragana"] },
  { key:"ro",  char:"ろ", romaji:"ro",    num:43, gridCol:2,  gridRow:5, groups:["hiragana"] },
  { key:"wa",  char:"わ", romaji:"wa",    num:44, gridCol:1,  gridRow:1, groups:["hiragana"] },
  { key:"wo",  char:"を", romaji:"o",     num:45, gridCol:1,  gridRow:5, groups:["hiragana"] },
  { key:"n",   char:"ん", romaji:"n (m)", num:46, gridCol:0,  gridRow:1, groups:["hiragana"] },
  { key:"a",   char:"ア", romaji:"a",     num:1,  gridCol:10, gridRow:1, groups:["katakana"] },
  { key:"i",   char:"イ", romaji:"i",     num:2,  gridCol:10, gridRow:2, groups:["katakana"] },
  { key:"u",   char:"ウ", romaji:"u",     num:3,  gridCol:10, gridRow:3, groups:["katakana"] },
  { key:"e",   char:"エ", romaji:"e",     num:4,  gridCol:10, gridRow:4, groups:["katakana"] },
  { key:"o",   char:"オ", romaji:"o",     num:5,  gridCol:10, gridRow:5, groups:["katakana"] },
  { key:"ka",  char:"カ", romaji:"ka",    num:6,  gridCol:9,  gridRow:1, groups:["katakana"] },
  { key:"ki",  char:"キ", romaji:"ki",    num:7,  gridCol:9,  gridRow:2, groups:["katakana"] },
  { key:"ku",  char:"ク", romaji:"ku",    num:8,  gridCol:9,  gridRow:3, groups:["katakana"] },
  { key:"ke",  char:"ケ", romaji:"ke",    num:9,  gridCol:9,  gridRow:4, groups:["katakana"] },
  { key:"ko",  char:"コ", romaji:"ko",    num:10, gridCol:9,  gridRow:5, groups:["katakana"] },
  { key:"sa",  char:"サ", romaji:"sa",    num:11, gridCol:8,  gridRow:1, groups:["katakana"] },
  { key:"shi", char:"シ", romaji:"shi",   num:12, gridCol:8,  gridRow:2, groups:["katakana"] },
  { key:"su",  char:"ス", romaji:"su",    num:13, gridCol:8,  gridRow:3, groups:["katakana"] },
  { key:"se",  char:"セ", romaji:"se",    num:14, gridCol:8,  gridRow:4, groups:["katakana"] },
  { key:"so",  char:"ソ", romaji:"so",    num:15, gridCol:8,  gridRow:5, groups:["katakana"] },
  { key:"ta",  char:"タ", romaji:"ta",    num:16, gridCol:7,  gridRow:1, groups:["katakana"] },
  { key:"chi", char:"チ", romaji:"chi",   num:17, gridCol:7,  gridRow:2, groups:["katakana"] },
  { key:"tsu", char:"ツ", romaji:"tsu",   num:18, gridCol:7,  gridRow:3, groups:["katakana"] },
  { key:"te",  char:"テ", romaji:"te",    num:19, gridCol:7,  gridRow:4, groups:["katakana"] },
  { key:"to",  char:"ト", romaji:"to",    num:20, gridCol:7,  gridRow:5, groups:["katakana"] },
  { key:"na",  char:"ナ", romaji:"na",    num:21, gridCol:6,  gridRow:1, groups:["katakana"] },
  { key:"ni",  char:"ニ", romaji:"ni",    num:22, gridCol:6,  gridRow:2, groups:["katakana"] },
  { key:"nu",  char:"ヌ", romaji:"nu",    num:23, gridCol:6,  gridRow:3, groups:["katakana"] },
  { key:"ne",  char:"ネ", romaji:"ne",    num:24, gridCol:6,  gridRow:4, groups:["katakana"] },
  { key:"no",  char:"ノ", romaji:"no",    num:25, gridCol:6,  gridRow:5, groups:["katakana"] },
  { key:"ha",  char:"ハ", romaji:"ha",    num:26, gridCol:5,  gridRow:1, groups:["katakana"] },
  { key:"hi",  char:"ヒ", romaji:"hi",    num:27, gridCol:5,  gridRow:2, groups:["katakana"] },
  { key:"fu",  char:"フ", romaji:"fu",    num:28, gridCol:5,  gridRow:3, groups:["katakana"] },
  { key:"he",  char:"ヘ", romaji:"he",    num:29, gridCol:5,  gridRow:4, groups:["katakana"] },
  { key:"ho",  char:"ホ", romaji:"ho",    num:30, gridCol:5,  gridRow:5, groups:["katakana"] },
  { key:"ma",  char:"マ", romaji:"ma",    num:31, gridCol:4,  gridRow:1, groups:["katakana"] },
  { key:"mi",  char:"ミ", romaji:"mi",    num:32, gridCol:4,  gridRow:2, groups:["katakana"] },
  { key:"mu",  char:"ム", romaji:"mu",    num:33, gridCol:4,  gridRow:3, groups:["katakana"] },
  { key:"me",  char:"メ", romaji:"me",    num:34, gridCol:4,  gridRow:4, groups:["katakana"] },
  { key:"mo",  char:"モ", romaji:"mo",    num:35, gridCol:4,  gridRow:5, groups:["katakana"] },
  { key:"ya",  char:"ヤ", romaji:"ya",    num:36, gridCol:3,  gridRow:1, groups:["katakana"] },
  { key:"yu",  char:"ユ", romaji:"yu",    num:37, gridCol:3,  gridRow:3, groups:["katakana"] },
  { key:"yo",  char:"ヨ", romaji:"yo",    num:38, gridCol:3,  gridRow:5, groups:["katakana"] },
  { key:"ra",  char:"ラ", romaji:"ra",    num:39, gridCol:2,  gridRow:1, groups:["katakana"] },
  { key:"ri",  char:"リ", romaji:"ri",    num:40, gridCol:2,  gridRow:2, groups:["katakana"] },
  { key:"ru",  char:"ル", romaji:"ru",    num:41, gridCol:2,  gridRow:3, groups:["katakana"] },
  { key:"re",  char:"レ", romaji:"re",    num:42, gridCol:2,  gridRow:4, groups:["katakana"] },
  { key:"ro",  char:"ロ", romaji:"ro",    num:43, gridCol:2,  gridRow:5, groups:["katakana"] },
  { key:"wa",  char:"ワ", romaji:"wa",    num:44, gridCol:1,  gridRow:1, groups:["katakana"] },
  { key:"wo",  char:"ヲ", romaji:"o",     num:45, gridCol:1,  gridRow:5, groups:["katakana"] },
  { key:"n",   char:"ン", romaji:"n (m)", num:46, gridCol:0,  gridRow:1, groups:["katakana"] },
];

const FILTERS = ["hiragana", "katakana"];

// ─── CharGrid ─────────────────────────────────────────────────────────────────
function CharGrid({ allChars, activeChars, selected, onSelect, completedChars, practiceMode, practiceComplete }) {
  if (!allChars.length) return null;
  const minCol    = Math.min(...allChars.map((c) => c.gridCol));
  const maxCol    = Math.max(...allChars.map((c) => c.gridCol));
  const maxRow    = Math.max(...allChars.map((c) => c.gridRow));
  const totalCols = maxCol - minCol + 1;
  const activeSet = new Set(activeChars.map((c) => c.char + c.groups[0]));
  const allMap    = {};
  allChars.forEach((c) => { allMap[`${c.gridCol}_${c.gridRow}`] = c; });

  const cells = [];
  for (let row = 1; row <= maxRow; row++)
    for (let col = minCol; col <= maxCol; col++)
      cells.push({ key: `${col}_${row}`, c: allMap[`${col}_${row}`] || null });

  return (
    <div className="grid gap-px" style={{ gridTemplateColumns: `repeat(${totalCols}, minmax(0,1fr))` }}>
      {cells.map(({ key, c }) =>
        c ? (
          <motion.button
            key={c.char + c.groups[0]}
            onClick={() => activeSet.has(c.char + c.groups[0]) && onSelect(c)}
            whileTap={activeSet.has(c.char + c.groups[0]) ? { scale: 0.9 } : {}}
            style={{ aspectRatio: "1/1" }}
            className={`flex flex-col items-center justify-center rounded-lg px-0.5 border transition-all overflow-hidden
              ${selected?.char === c.char && selected?.groups[0] === c.groups[0]
                ? "border-[#2797c4] bg-[#2797c4]/10"
                : activeSet.has(c.char + c.groups[0])
                  ? completedChars?.[c.char + c.groups[0]]
                    ? "border-[#2797c4]/60 bg-white hover:bg-[#2797c4]/10 cursor-pointer"
                    : "border-[#fdcb6e]/60 bg-white hover:bg-[#fdcb6e]/10 hover:border-[#fdcb6e] cursor-pointer"
                  : "border-transparent bg-gray-50 opacity-20 cursor-default pointer-events-none"
              }`}
          >
            {completedChars?.[c.char + c.groups[0]] ? (
              <img
                src={completedChars[c.char + c.groups[0]].dataUrl}
                alt={c.char}
                className="w-full h-full object-contain"
                style={{ minHeight: "1.5rem" }}
              />
            ) : (
              <>
                <span className="text-xs sm:text-sm font-bold text-gray-800 leading-none" style={{ fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>{c.char}</span>
                <span className="text-[7px] sm:text-[9px] text-gray-400 leading-none mt-0.5">{c.romaji}</span>
              </>
            )}
          </motion.button>
        ) : (
          <div key={key} />
        )
      )}
    </div>
  );
}

// ─── Chroma-key video player (removes green background frame-by-frame) ────────
function HanamaruPlayer({ src, onComplete }) {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const rafRef    = useRef(null);

  useEffect(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    const drawFrame = () => {
      if (!video || video.ended) return;
      // Sync canvas pixel dimensions to video (once available)
      if (video.videoWidth > 0 && canvas.width !== video.videoWidth) {
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
      }
      if (canvas.width > 0 && canvas.height > 0 && !video.paused) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const d   = img.data;
        for (let i = 0; i < d.length; i += 4) {
          const r = d[i], g = d[i + 1], b = d[i + 2];
          // Chroma-key: green channel dominant by ≥30 over both r and b
          if (g > 80 && g - r > 30 && g - b > 30) d[i + 3] = 0;
        }
        ctx.putImageData(img, 0, 0);
      }
      rafRef.current = requestAnimationFrame(drawFrame);
    };

    const startLoop = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(drawFrame);
    };

    const onEnded = () => {
      cancelAnimationFrame(rafRef.current);
      // Freeze last frame for 5 s then hide
      setTimeout(() => onComplete?.(), 5000);
    };

    video.addEventListener("play",    startLoop);
    video.addEventListener("playing", startLoop);
    video.addEventListener("ended",   onEnded);

    // autoPlay may have already fired before this effect runs
    if (!video.paused && !video.ended) startLoop();
    // If not yet playing, let the browser autoPlay trigger it
    else if (video.paused && !video.ended) video.play().catch(() => {});

    return () => {
      cancelAnimationFrame(rafRef.current);
      video.removeEventListener("play",    startLoop);
      video.removeEventListener("playing", startLoop);
      video.removeEventListener("ended",   onEnded);
    };
  }, [src, onComplete]);

  return (
    <>
      <video ref={videoRef} src={src} autoPlay muted playsInline style={{ display: "none" }} />
      <canvas ref={canvasRef} width={480} height={480} style={{ width: "min(70vw, 380px)", height: "auto" }} />
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const STROKE_PAGE_META = {
  EN: {
    title: "Hiragana & Katakana Stroke Order Practice | Hanapin",
    desc:  "Practice hiragana and katakana stroke order online with animated guides and interactive writing exercises.",
  },
  JP: {
    title: "ひらがな・カタカナ　書き順練習 | はなぴん",
    desc:  "オンラインでひらがなとカタカナの書き順練習ができます！",
  },
  ZH: {
    title: "平假名・片假名书写顺序练习 | 樱花老师",
    desc:  "在线练习平假名和片假名的书写顺序，附动画演示和互动练习！",
  },
};

export default function StrokeOrder2({ language = "EN" }) {
  const t = I18N[language] || I18N.EN;

  // ── Core UI state ────────────────────────────────────────────────────────
  const [selected,        setSelected]        = useState(null);
  const [activeFilters,   setActiveFilters]   = useState(["hiragana"]);
  const [search,          setSearch]          = useState("");
  const [loading,         setLoading]         = useState(false);
  const [loadError,       setLoadError]       = useState(null);
  const [animStatus,      setAnimStatus]      = useState("idle");
  const [strokeCount,     setStrokeCount]     = useState(0);
  const [currentStroke,   setCurrentStroke]   = useState(0);
  const [speedMult,       setSpeedMult]       = useState(1.0);
  const [playAllActive,   setPlayAllActive]   = useState(false);
  const [playAllProgress, setPlayAllProgress] = useState(null);
  const [playAllSaved,    setPlayAllSaved]    = useState(null);

  // ── Play mode tab: "play" | "practice" ──────────────────────────────────
  const [playModeTab,       setPlayModeTab]       = useState("play");

  // ── Practice state ───────────────────────────────────────────────────────
  const [practiceMode,      setPracticeMode]      = useState(false);
  const [practiceStrokeIdx, setPracticeStrokeIdx] = useState(0);
  const [practiceComplete,  setPracticeComplete]  = useState(false);
  const [practiceAllActive, setPracticeAllActive] = useState(false);

  // ── Completed chars: { charId: { dataUrl, strokes } } ───────────────────
  const [completedChars,    setCompletedChars]    = useState({});

  // ── All-practice completion screen ──────────────────────────────────────
  const [allPracticeComplete, setAllPracticeComplete] = useState(false);
  const [compositeImageUrl,   setCompositeImageUrl]   = useState(null);
  const [showCompositePopup,  setShowCompositePopup]  = useState(false);
  const [showHanamaru,        setShowHanamaru]        = useState(false);
  const hanamaruShownRef  = useRef(false);
  const goodjobPlayedRef  = useRef(false);

  // ── Per-page SEO ──────────────────────────────────────────────────────────
  useEffect(() => {
    const meta = STROKE_PAGE_META[language] || STROKE_PAGE_META.EN;
    document.title = meta.title;
    const el = document.querySelector('meta[name="description"]');
    if (el) el.setAttribute("content", meta.desc);
  }, [language]);

  // ── Animation/audio refs ─────────────────────────────────────────────────
  const canvasRef        = useRef(null);
  const rafRef           = useRef(null);
  const animRef          = useRef(null);
  const audioRef         = useRef(null);
  const correctAudioRef  = useRef(null); // track so we can stop before replaying
  const charAudioRef = useRef(null);
  const speedMultRef = useRef(1.0);
  const playAllRef   = useRef({ active: false, chars: [], idx: 0 });

  // ── Practice refs ────────────────────────────────────────────────────────
  const practiceCanvasRef    = useRef(null);
  const practiceStrokesRef   = useRef([]);
  const practiceStrokeIdxRef = useRef(0);
  const isDrawingRef         = useRef(false);
  const drawProgressRef      = useRef(0);
  const currentStrokesRef    = useRef([]);     // SVG strokes for current char (for practice again)
  const selectedRef          = useRef(null);   // mirrors selected state for callbacks
  const onPracticeCompleteRef= useRef(null);   // called when char practice finishes (for practice-all)
  const practiceSeqRef       = useRef({ active: false, chars: [], idx: 0 });
  const practiceStartTimeRef = useRef(null);   // timestamp when first practice began
  const logoImgRef           = useRef(null);
  const logoTextImgRef       = useRef(null);

  useEffect(() => { speedMultRef.current = speedMult; }, [speedMult]);

  // Preload logos for composite image
  useEffect(() => {
    [[ _logoSrc, logoImgRef ], [ _logoTextSrc, logoTextImgRef ]].forEach(([src, ref]) => {
      const img = new Image(); img.onload = () => { ref.current = img; }; img.src = src;
    });
  }, []);
  useEffect(() => { selectedRef.current  = selected;  }, [selected]);

  useEffect(() => {
    const audio = new Audio(STROKE_SOUND_URL);
    audio.loop = true; audioRef.current = audio;
    return () => { audio.pause(); audio.src = ""; };
  }, []);

  // ── Play goodjob audio ───────────────────────────────────────────────────
  const playGoodjob = useCallback(() => {
    const url = GOODJOB_AUDIO[language.toLowerCase()] || GOODJOB_AUDIO.en;
    if (url) { const a = new Audio(url); a.play().catch(() => {}); }
  }, [language]);

  // ── Practice audio helpers ───────────────────────────────────────────────
  const playTap = useCallback(() => {
    const url = CHAR_AUDIO_MAP["tap"];
    if (!url) return;
    const a = new Audio(url); a.volume = 0.5; a.play().catch(() => {});
  }, []);

  const playCorrect = useCallback((onDone) => {
    // Stop any still-playing correct sound so mobile doesn't queue/drop the new one
    if (correctAudioRef.current) {
      correctAudioRef.current.pause();
      correctAudioRef.current = null;
    }
    const url = CHAR_AUDIO_MAP["correct"];
    if (url) {
      const a = new Audio(url);
      correctAudioRef.current = a;
      if (onDone) a.onended = () => { correctAudioRef.current = null; onDone(); };
      a.play().catch(() => { if (onDone) onDone(); });
      return a;
    }
    // Fallback: ascending two-tone ding
    playBeep(660, 0.08, 0.2);
    setTimeout(() => { playBeep(880, 0.10, 0.2); if (onDone) setTimeout(onDone, 120); }, 80);
    return null;
  }, []);

  // Play "stroke" sound after each individual stroke (file: assets/audio/stroke.mp3)
  const playStroke = useCallback(() => {
    const url = CHAR_AUDIO_MAP["stroke"];
    if (url) {
      const a = new Audio(url); a.play().catch(() => {});
    } else {
      playBeep(520, 0.07, 0.15); // soft mid-tone fallback
    }
  }, []);

  const playCharSound = useCallback((char) => {
    const url = CHAR_AUDIO_MAP[char?.key];
    if (!url) return null;
    const a = new Audio(url); a.play().catch(() => {});
    return a; // return for onended chaining
  }, []);

  // ── Render practice overlay (SVG-path strokes + blue dot) ───────────────
  const renderPractice = useCallback(() => {
    const pCanvas = practiceCanvasRef.current;
    if (!pCanvas) return;
    const ctx     = pCanvas.getContext("2d");
    const sIdx    = practiceStrokeIdxRef.current;
    const strokes = practiceStrokesRef.current;
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);


    // Draw completed strokes in blue using the exact SVG path
    for (let i = 0; i < sIdx; i++) {
      drawStroke(ctx, strokes[i], 1, PRACTICE_BLUE);
    }

    // Draw current stroke in progress (finger drags it forward along SVG path)
    if (isDrawingRef.current && sIdx < strokes.length) {
      drawStroke(ctx, strokes[sIdx], drawProgressRef.current, PRACTICE_BLUE);
      drawStrokeIndicator(ctx, strokes[sIdx], sIdx + 1);
    }

    // Blue dot + direction indicator at start of current stroke
    if (sIdx < strokes.length && !isDrawingRef.current) {
      const pt = strokes[sIdx].pts[0];
      ctx.save();
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, PINK_DOT_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = PRACTICE_BLUE;
      ctx.fill();
      ctx.restore();
      drawStrokeIndicator(ctx, strokes[sIdx], sIdx + 1);
    }
  }, []);

  // ── Enter practice mode after animation ──────────────────────────────────
  const enterPracticeMode = useCallback((strokes) => {
    practiceStrokesRef.current   = strokes;
    practiceStrokeIdxRef.current = 0;
    drawProgressRef.current      = 0;
    isDrawingRef.current         = false;
    setPracticeMode(true);
    setPracticeStrokeIdx(0);
    setPracticeComplete(false);

    // Imperatively enable pointer events NOW — don't wait for React re-render,
    // otherwise the canvas stays pointerEvents:none when the dot first appears.
    const pCanvas = practiceCanvasRef.current;
    if (pCanvas) {
      pCanvas.style.pointerEvents = "auto";
      pCanvas.style.touchAction   = "none";
    }

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      clearCanvas(ctx);
      strokes.forEach((s) => drawStroke(ctx, s, 1, STROKE_GRAY_COLOR));
    }
    if (!practiceStartTimeRef.current) practiceStartTimeRef.current = Date.now();
    setTimeout(() => renderPractice(), 0);
  }, [renderPractice]);

  // ── Capture completed char as mini image ──────────────────────────────────
  const captureChar = useCallback(() => {
    const char    = selectedRef.current;
    const pCanvas = practiceCanvasRef.current;
    if (!char || !pCanvas) return;
    const charId   = char.char + char.groups[0];
    const dataUrl = pCanvas.toDataURL("image/png");
    const strokes = practiceStrokesRef.current;
    setCompletedChars(prev => ({ ...prev, [charId]: { dataUrl, strokes } }));
  }, []);

  // ── Generate composite image of all completed chars ───────────────────────
  // gridChars = full grid for sizing; completed = only drawn chars
  const generateCompositeImage = useCallback((gridChars, completed, elapsedMins, now) => {
    // ── Canvas: 16:9 landscape ────────────────────────────────────────────
    const W = 1920, H = 1080;
    const off = document.createElement("canvas");
    off.width = W; off.height = H;
    const ctx = off.getContext("2d");

    if (!gridChars.length) return null;

    const minCol = Math.min(...gridChars.map(c => c.gridCol));
    const maxCol = Math.max(...gridChars.map(c => c.gridCol));
    const minRow = Math.min(...gridChars.map(c => c.gridRow));
    const maxRow = Math.max(...gridChars.map(c => c.gridRow));
    const cols   = maxCol - minCol + 1;
    const rows   = maxRow - minRow + 1;
    const totalCount = gridChars.length;

    // ── Layout constants ──────────────────────────────────────────────────
    const MARGIN_X  = 40;
    const TOP_PAD   = 30;
    const TITLE_H   = 110;
    const LOGO_H    = 48;
    const LOGO_PAD  = 14;
    const GRID_LINE = 1.5;
    const ROMAJI_H  = 28;   // romaji strip at top of each cell

    const gridTop  = TOP_PAD + TITLE_H + 10;
    const gridBot  = H - LOGO_H - LOGO_PAD * 2;
    const availW   = W - MARGIN_X * 2;
    const availH   = gridBot - gridTop;

    const CELL_W   = Math.floor((availW - (cols + 1) * GRID_LINE) / cols);
    const CELL_H   = Math.floor((availH - (rows + 1) * GRID_LINE) / rows);
    const SQUARE   = Math.min(CELL_W, CELL_H - ROMAJI_H);  // square drawing area
    const gridW    = cols * CELL_W + (cols + 1) * GRID_LINE;
    const gridH    = rows * CELL_H + (rows + 1) * GRID_LINE;
    const gridX    = Math.round((W - gridW) / 2);
    const gridY    = gridTop;

    // ── Background ────────────────────────────────────────────────────────
    ctx.fillStyle = "#fffaf0";
    ctx.fillRect(0, 0, W, H);

    // ── Title (non-bold, left-aligned) ────────────────────────────────────
    const scriptName = gridChars[0]?.groups?.includes("katakana") ? "カタカナ" : "ひらがな";
    const mins       = elapsedMins ?? 0;

    // ぷん/ふん helper: 0→ふん; unit in {1,3,4,6,8} or multiples of 10 (>0)→ぷん
    const toFunPun = (n) => {
      if (n === 0) return "ふん";
      const u = n % 10;
      return (u === 1 || u === 3 || u === 4 || u === 6 || u === 8 || u === 0) ? "ぷん" : "ふん";
    };

    ctx.textBaseline = "middle";
    // Line 1: "すごい！"
    ctx.font      = "60px system-ui, 'Hiragino Kaku Gothic ProN', sans-serif";
    ctx.fillStyle = "#b5435f";
    ctx.textAlign = "left";
    ctx.fillText("すごい！", MARGIN_X, TOP_PAD + 32);

    // Line 2: time + chars
    ctx.font      = "38px system-ui, 'Hiragino Kaku Gothic ProN', sans-serif";
    ctx.fillStyle = "#444444";
    ctx.fillText(
      `${mins}${toFunPun(mins)}で${scriptName}${totalCount}文字をぜんぶれんしゅうしたよ！`,
      MARGIN_X, TOP_PAD + 82
    );

    // ── Date/time — upper right ───────────────────────────────────────────
    if (now) {
      const DAYS_HI = ["にち","げつ","か","すい","もく","きん","ど"];
      const h = now.getHours(), m2 = now.getMinutes();
      const dateStr = `${now.getFullYear()}ねん${now.getMonth()+1}がつ${now.getDate()}にち${DAYS_HI[now.getDay()]}ようび　${h}じ${m2}${toFunPun(m2)}`;
      ctx.font      = "30px system-ui, sans-serif";
      ctx.fillStyle = "#aaaaaa";
      ctx.textAlign = "right";
      ctx.fillText(dateStr, W - MARGIN_X, TOP_PAD + 32);
    }

    // ── Grid background ───────────────────────────────────────────────────
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(gridX, gridY, gridW, gridH);

    // ── Cell contents ─────────────────────────────────────────────────────
    gridChars.forEach((char) => {
      const charId = char.char + char.groups[0];
      const data   = completed[charId];
      const col    = char.gridCol - minCol;
      const row    = char.gridRow - minRow;
      const cx     = gridX + GRID_LINE + col * (CELL_W + GRID_LINE);
      const cy     = gridY + GRID_LINE + row * (CELL_H + GRID_LINE);

      // Cell background
      ctx.fillStyle = data ? "#fafafa" : "f8f8f8";
      ctx.fillRect(cx, cy, CELL_W, CELL_H);

      // Romaji — above the drawing, bigger
      ctx.font      = `${Math.round(ROMAJI_H * 0.72)}px system-ui, sans-serif`;
      ctx.fillStyle = data ? "#999999" : "#cccccc";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(char.romaji, cx + CELL_W / 2, cy + ROMAJI_H / 2);

      // Square drawing area (centered horizontally, below romaji)
      const sqX = cx + Math.round((CELL_W - SQUARE) / 2);
      const sqY = cy + ROMAJI_H;
      const drawScale = SQUARE / CANVAS_SIZE;

      ctx.save();
      ctx.beginPath();
      ctx.rect(sqX, sqY, SQUARE, SQUARE);
      ctx.clip();
      ctx.translate(sqX, sqY);
      ctx.scale(drawScale, drawScale);

      if (data?.strokes?.length) {
        data.strokes.forEach(s => drawStroke(ctx, s, 1, "#000000"));
      } else {
        // Not yet practiced: faint character
        ctx.font = `${CANVAS_SIZE * 0.55}px system-ui, 'Hiragino Kaku Gothic ProN', sans-serif`;
        ctx.fillStyle = "#eeeeee";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(char.char, CANVAS_SIZE / 2, CANVAS_SIZE / 2);
      }
      ctx.restore();
    });

    // ── Blue grid lines ───────────────────────────────────────────────────
    ctx.strokeStyle = "#2797c4";
    ctx.lineWidth   = GRID_LINE;
    for (let c = 0; c <= cols; c++) {
      const x = gridX + c * (CELL_W + GRID_LINE) + GRID_LINE / 2;
      ctx.beginPath(); ctx.moveTo(x, gridY); ctx.lineTo(x, gridY + gridH); ctx.stroke();
    }
    for (let r = 0; r <= rows; r++) {
      const y = gridY + r * (CELL_H + GRID_LINE) + GRID_LINE / 2;
      ctx.beginPath(); ctx.moveTo(gridX, y); ctx.lineTo(gridX + gridW, y); ctx.stroke();
    }

    // ── Logo — bottom left ────────────────────────────────────────────────
    const logoY = gridBot + LOGO_PAD;
    if (logoImgRef.current) {
      const lRatio = logoImgRef.current.width / logoImgRef.current.height;
      const lW = LOGO_H * lRatio;
      ctx.drawImage(logoImgRef.current, MARGIN_X, logoY, lW, LOGO_H);
      if (logoTextImgRef.current) {
        const ltRatio = logoTextImgRef.current.width / logoTextImgRef.current.height;
        ctx.drawImage(logoTextImgRef.current, MARGIN_X + lW + 6, logoY, LOGO_H * ltRatio, LOGO_H);
      }
    } else {
      ctx.font = `bold ${LOGO_H * 0.7}px system-ui, sans-serif`;
      ctx.fillStyle = "#b5435f";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText("hanapin", MARGIN_X, logoY + LOGO_H / 2);
    }

    return off.toDataURL("image/png");
  }, []);

  // ── Auto-generate composite whenever completedChars changes ────────────────
  useEffect(() => {
    const ids = Object.keys(completedChars);
    if (ids.length === 0) { setCompositeImageUrl(null); return; }
    const ordered = CHARS.filter(c => completedChars[c.char + c.groups[0]]);
    // Use all chars from same groups for correct grid ratio (same as button layout)
    const groups = new Set(ordered.flatMap(c => c.groups));
    const gridChars = CHARS.filter(c => c.groups.some(g => groups.has(g)));
    const elapsed = practiceStartTimeRef.current
      ? Math.max(1, Math.round((Date.now() - practiceStartTimeRef.current) / 60000))
      : 0;
    const img = generateCompositeImage(gridChars, completedChars, elapsed, new Date());
    setCompositeImageUrl(img);
    if (ids.length >= 46) {
      setAllPracticeComplete(true);
      setShowCompositePopup(true);
      if (!goodjobPlayedRef.current) {
        goodjobPlayedRef.current = true;
        const gUrl = GOODJOB_AUDIO[language.toLowerCase()] || GOODJOB_AUDIO.en;
        if (gUrl) { const a = new Audio(gUrl); a.play().catch(() => {}); }
      }
      if (!hanamaruShownRef.current) {
        hanamaruShownRef.current = true;
        setShowHanamaru(true);
      }
    }
  }, [completedChars, generateCompositeImage, language]);

    // ── Clear practice overlay ────────────────────────────────────────────────
  const clearPractice = useCallback(() => {
    setPracticeMode(false);
    setPracticeComplete(false);
    setAllPracticeComplete(false);
    practiceStrokesRef.current    = [];
    practiceStrokeIdxRef.current  = 0;
    drawProgressRef.current       = 0;
    isDrawingRef.current          = false;
    onPracticeCompleteRef.current = null;
    const pCanvas = practiceCanvasRef.current;
    if (pCanvas) {
      pCanvas.getContext("2d").clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      // Imperatively disable pointer events to match state
      pCanvas.style.pointerEvents = "none";
      pCanvas.style.touchAction   = "auto";
    }
    hanamaruShownRef.current = false;
    goodjobPlayedRef.current = false;
  }, []);

  // ── Stop everything ───────────────────────────────────────────────────────
  const stopAll = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    animRef.current = null;
    if (audioRef.current)     audioRef.current.pause();
    if (charAudioRef.current) { charAudioRef.current.pause(); charAudioRef.current.currentTime = 0; }
    playAllRef.current.active    = false;
    practiceSeqRef.current.active = false;
    setPlayAllActive(false);
    setPlayAllProgress(null);
    setPracticeAllActive(false);
    clearPractice();
  }, [clearPractice]);

  // ── Animation loop ────────────────────────────────────────────────────────
  const runLoop = useCallback((strokes, onComplete) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx   = canvas.getContext("2d");
    const total = strokes.length;

    animRef.current = { phase: "pink_dot", strokeIdx: 0, phaseStart: performance.now(), completedUpTo: -1 };
    setAnimStatus("running"); setCurrentStroke(1);

    const frame = (now) => {
      const a = animRef.current;
      if (!a) return;
      const elapsed = now - a.phaseStart;
      const idx     = a.strokeIdx;
      const stroke  = strokes[idx];
      const spd     = speedMultRef.current;
      const drawDur = stroke.length / BASE_PX_MS / spd;
      const dotDur  = PINK_DOT_DURATION / spd;

      clearCanvas(ctx);
      for (let i = 0; i <= a.completedUpTo; i++) drawStroke(ctx, strokes[i], 1, STROKE_DONE_COLOR);

      if (a.phase === "pink_dot") {
        if (audioRef.current) audioRef.current.pause();
        const p0 = stroke.pts[0];
        drawPinkDot(ctx, { x: p0.x / CANVAS_SIZE, y: p0.y / CANVAS_SIZE });
        drawStrokeIndicator(ctx, stroke, idx + 1);
        if (elapsed >= dotDur) { a.phase = "drawing"; a.phaseStart = now; }

      } else if (a.phase === "drawing") {
        if (audioRef.current && audioRef.current.paused) audioRef.current.play().catch(() => {});
        const prog = Math.min(elapsed / drawDur, 1);
        drawStroke(ctx, stroke, prog, STROKE_DRAW_COLOR);
        drawStrokeIndicator(ctx, stroke, idx + 1);
        if (elapsed >= drawDur) { a.completedUpTo = idx; a.phase = "pausing"; a.phaseStart = now; }

      } else if (a.phase === "pausing") {
        if (audioRef.current) audioRef.current.pause();
        if (elapsed >= PAUSE_DURATION) {
          const next = idx + 1;
          if (next >= total) {
            setAnimStatus("done"); setCurrentStroke(total);
            if (audioRef.current) audioRef.current.pause();
            a.phase = "end_pause"; a.phaseStart = now;
          } else {
            a.strokeIdx = next; a.phase = "pink_dot"; a.phaseStart = now;
            setCurrentStroke(next + 1);
          }
        }
      } else if (a.phase === "end_pause") {
        if (elapsed >= END_PAUSE / spd) {
          animRef.current = null;
          if (onComplete) onComplete(strokes);
          return;
        }
      }

      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
  }, []);

  // ── Load SVG + play one character ─────────────────────────────────────────
  const loadAndPlayChar = useCallback(async (char, onComplete, skipAudio = false) => {
    clearPractice();
    const canvas = canvasRef.current;
    if (canvas) clearCanvas(canvas.getContext("2d"));
    setLoading(true); setLoadError(null); setAnimStatus("idle");

    if (!skipAudio) {
      const audioUrl = CHAR_AUDIO_MAP[char.key];
      if (audioUrl) {
        const a = new Audio(audioUrl);
        charAudioRef.current = a;
        a.play().catch(() => {});
      } else {
        charAudioRef.current = null;
      }
    }

    try {
      const src = getSVGSource(char);
      if (!src) {
        setLoading(false);
        const prefix = char.groups.includes("katakana") ? "katakana" : "hiragana";
        setLoadError(`No SVG found. Add ${prefix}_${char.key}.svg to src/assets/svg/`);
        if (onComplete) onComplete([]);
        return;
      }
      const strokes = await loadSVGStrokes(src.url, src.charKey);
      if (!strokes.length) {
        setLoading(false);
        setLoadError(`No strokes found for "${char.char}". Check <g id="stroke_1"> groups.`);
        if (onComplete) onComplete([]);
        return;
      }
      currentStrokesRef.current = strokes;
      setStrokeCount(strokes.length);
      setLoading(false);
      // onComplete receives strokes; if null, default to entering practice mode
      const effectiveComplete = (s) => {
        if (onComplete) onComplete(s);
        else enterPracticeMode(s);
      };
      runLoop(strokes, effectiveComplete);
    } catch (err) {
      setLoading(false);
      setLoadError(err.message || "Failed to load SVG.");
      if (onComplete) onComplete([]);
    }
  }, [runLoop, clearPractice, enterPracticeMode]);

  // ── Practice All sequence (no animation — load strokes then go straight to practice) ──
  const startPracticeAll = useCallback((chars, startIdx = 0) => {
    practiceSeqRef.current = { active: true, chars, idx: startIdx };
    setPracticeAllActive(true);
    setAllPracticeComplete(false);
    setCompositeImageUrl(null);

    const playNextPractice = (idx) => {
      if (!practiceSeqRef.current.active) return;
      if (idx >= chars.length) {
        practiceSeqRef.current.active = false;
        setPracticeAllActive(false);
        return; // goodjob plays via completedChars useEffect when popup opens
      }

      const char = chars[idx];
      setSelected(char); setStrokeCount(0); setCurrentStroke(0);

      // Play char sound first, then start animation once sound ends
      const charAudio = playCharSound(char);
      const startAnim = () => {
        if (!practiceSeqRef.current.active) return;
        // Play animation; when animation finishes set the advance callback
        // THEN enter practice mode — must be set after loadAndPlayChar calls
        // clearPractice() (which would null the ref if set earlier)
        loadAndPlayChar(char, (strokes) => {
          if (!practiceSeqRef.current.active) return;
          // If SVG failed to load, skip this char and continue
          if (!strokes.length) {
            practiceSeqRef.current.idx = idx + 1;
            playNextPractice(idx + 1);
            return;
          }
          onPracticeCompleteRef.current = () => {
            practiceSeqRef.current.idx = idx + 1;
            playNextPractice(idx + 1);
          };
          enterPracticeMode(strokes);
        }, true); // skipAudio — char sound already played before animation
      };
      if (charAudio) {
        let fired = false;
        const fireOnce = () => { if (!fired) { fired = true; startAnim(); } };
        charAudio.onended = fireOnce;
        setTimeout(fireOnce, 4000); // failsafe — won't re-fire if onended already ran
      } else {
        startAnim();
      }
    };

    playNextPractice(startIdx);
  }, [loadAndPlayChar, enterPracticeMode, playGoodjob, playCharSound]);

  // ── Play All sequence ─────────────────────────────────────────────────────
  const startPlayAll = useCallback((chars, startIdx) => {
    playAllRef.current = { active: true, chars, idx: startIdx };
    setPlayAllActive(true);
    setPlayAllProgress({ idx: startIdx, total: chars.length });

    const playNext = () => {
      if (!playAllRef.current.active) return;
      const { chars: ch, idx } = playAllRef.current;
      if (idx >= ch.length) {
        playAllRef.current.active = false;
        setPlayAllActive(false); setPlayAllProgress(null); setPlayAllSaved(null);
        return;
      }
      const char = ch[idx];
      setSelected(char); setStrokeCount(0); setCurrentStroke(0);
      setPlayAllProgress({ idx, total: ch.length });

      const startAnim = () => {
        if (!playAllRef.current.active) return;
        loadAndPlayChar(char, () => {
          if (!playAllRef.current.active) return;
          playAllRef.current.idx = idx + 1;
          playNext();
        }, true); // skipAudio — char sound already played before animation
      };

      const charAudio = playCharSound(char);
      if (charAudio) {
        let fired = false;
        const fireOnce = () => { if (!fired) { fired = true; startAnim(); } };
        charAudio.onended = fireOnce;
        setTimeout(fireOnce, 4000); // failsafe — won't re-fire if onended already ran
      } else {
        startAnim();
      }
    };

    playNext();
  }, [loadAndPlayChar, playCharSound]);

  // ── Derived: visible + filtered chars (needed before handlePlayAll) ────────
  const visibleChars = useMemo(() => CHARS.filter((c) => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q || c.char.includes(q) || c.romaji.includes(q) || String(c.num).includes(q);
    const matchFilter = activeFilters.length === 0 || activeFilters.some((f) => c.groups.includes(f));
    return matchSearch && matchFilter;
  }), [activeFilters, search]);

  const allFilteredChars = useMemo(() =>
    CHARS.filter((c) => activeFilters.length === 0 || activeFilters.some((f) => c.groups.includes(f))),
  [activeFilters]);

  const hiraganaAll    = useMemo(() => allFilteredChars.filter(c => !c.groups.includes("katakana")), [allFilteredChars]);
  const katakanaAll    = useMemo(() => allFilteredChars.filter(c =>  c.groups.includes("katakana")), [allFilteredChars]);
  const hiraganaActive = useMemo(() => visibleChars.filter(c => !c.groups.includes("katakana")), [visibleChars]);
  const katakanaActive = useMemo(() => visibleChars.filter(c =>  c.groups.includes("katakana")), [visibleChars]);

  // ── Play All / Practice All button handler ────────────────────────────────
  // explicitMode: pass "play" or "practice" to override current playModeTab
  const handlePlayAll = useCallback((explicitMode) => {
    const mode = explicitMode ?? playModeTab;

    // If either sequence is running → stop
    if (playAllRef.current.active || practiceSeqRef.current.active) {
      if (playAllRef.current.active) {
        setPlayAllSaved({ chars: playAllRef.current.chars, idx: playAllRef.current.idx });
      }
      stopAll();
      return;
    }

    stopAll();

    const sortFn = (a, b) => {
      if (a.groups[0] !== b.groups[0]) return a.groups[0].localeCompare(b.groups[0]);
      return a.num - b.num;
    };

    if (mode === "practice") {
      // Practice All always uses ALL script chars (not search-filtered)
      if (!allFilteredChars.length) return;
      const chars = [...allFilteredChars].sort(sortFn);
      let startIdx = 0;
      if (selected) {
        const idx = chars.findIndex(c => c.char === selected.char && c.groups[0] === selected.groups[0]);
        if (idx !== -1) startIdx = idx;
      }
      startPracticeAll(chars, startIdx);
    } else {
      // Play All mode — uses visible (search-filtered) chars
      if (!visibleChars.length) return;
      const chars = [...visibleChars].sort(sortFn);
      if (playAllSaved && mode === "play") {
        const { chars: savedChars, idx } = playAllSaved;
        setPlayAllSaved(null);
        startPlayAll(savedChars, idx);
      } else {
        let startIdx = 0;
        if (selected) {
          const idx = chars.findIndex(c => c.char === selected.char && c.groups[0] === selected.groups[0]);
          if (idx !== -1) startIdx = idx;
        }
        startPlayAll(chars, startIdx);
      }
    }
  }, [playModeTab, playAllSaved, visibleChars, allFilteredChars, selected, stopAll, startPlayAll, startPracticeAll]);

  // ── Select a char ─────────────────────────────────────────────────────────
  const handleSelect = useCallback((c) => {
    stopAll();
    setSelected(c); setStrokeCount(0); setCurrentStroke(0);
    loadAndPlayChar(c, null);
  }, [stopAll, loadAndPlayChar]);

  // ── Play / Replay / Practice Again button ─────────────────────────────────
  const handlePlayBtn = useCallback(() => {
    if (!selected) return;
    playTap();
    if (practiceComplete) {
      // Practice Again: restart practice with stored strokes
      enterPracticeMode(currentStrokesRef.current);
      return;
    }
    // Replay animation
    clearPractice();
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    animRef.current = null;
    loadAndPlayChar(selected, null);
  }, [playTap, selected, practiceComplete, clearPractice, enterPracticeMode, loadAndPlayChar]);

  // ── Practice pointer handlers ─────────────────────────────────────────────
  const handlePointerDown = useCallback((e) => {
    const strokes = practiceStrokesRef.current;
    const sIdx    = practiceStrokeIdxRef.current;
    if (!strokes.length || sIdx >= strokes.length) return;

    const pos     = getCanvasPos(e, practiceCanvasRef.current);
    const stroke  = strokes[sIdx];
    const startPt = stroke.pts[0];
    // Accept touch near the start dot OR near any point of the stroke itself.
    // Only allow starting from the dot (beginning of stroke). Use a larger tap area
    // (PRACTICE_TAP_SCALE) so the touch target is comfortable even with the visual dot at 1×.
    const distToStart = Math.hypot(pos.x - startPt.x, pos.y - startPt.y);
    if (distToStart > PINK_DOT_RADIUS * PRACTICE_TAP_SCALE) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawingRef.current    = true;
    drawProgressRef.current = 0;
    renderPractice();
  }, [renderPractice]);

  const handlePointerMove = useCallback((e) => {
    if (!isDrawingRef.current) return;
    const strokes = practiceStrokesRef.current;
    const sIdx    = practiceStrokeIdxRef.current;
    if (sIdx >= strokes.length) return;

    const pos    = getCanvasPos(e, practiceCanvasRef.current);
    const stroke = strokes[sIdx];

    // Project finger forward along SVG path — only advance if close enough to the path
    const { pct, dist } = projectOnStroke(pos, stroke, drawProgressRef.current);
    if (dist <= stroke.lineWidth * PRACTICE_MOVE_TOL) {
      drawProgressRef.current = pct;
    }
    renderPractice();
  }, [renderPractice]);

  const handlePointerUp = useCallback((e) => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;

    const strokes  = practiceStrokesRef.current;
    const sIdx     = practiceStrokeIdxRef.current;
    const progress = drawProgressRef.current;

    if (progress >= PRACTICE_THRESHOLD) {
      const nextIdx = sIdx + 1;
      practiceStrokeIdxRef.current = nextIdx;
      drawProgressRef.current      = 0;
      setPracticeStrokeIdx(nextIdx);

      if (nextIdx >= strokes.length) {
        // All strokes done — chain: correct ding → char sound → advance
        renderPractice();
        captureChar();
        setPracticeComplete(true);
        const doAdvance = () => {
          const cb = onPracticeCompleteRef.current;
          if (cb) { onPracticeCompleteRef.current = null; cb(); }
        };
        playCorrect(() => {
          // correct ding finished → now play char sound
          const charAudio = playCharSound(selectedRef.current);
          if (charAudio) {
            let fired = false;
            const once = () => { if (!fired) { fired = true; doAdvance(); } };
            charAudio.onended = once;
            setTimeout(once, 3000);
          } else {
            setTimeout(doAdvance, 800);
          }
        });
      } else {
        // Intermediate stroke — play stroke sound (not correct)
        playStroke();
        renderPractice();
      }
    } else {
      drawProgressRef.current = 0;
      renderPractice();
    }
  }, [renderPractice, playCorrect, playStroke, playCharSound, captureChar]);

  // Filter change
  const toggleFilter = (f) => {
    playTap(); stopAll(); setPlayAllSaved(null); setActiveFilters([f]);
  };

  // Auto-select あ on first mount if nothing selected
  useEffect(() => {
    const first = CHARS.find(c => c.char === "あ") || CHARS[0];
    if (first) {
      setSelected(first);
      loadAndPlayChar(first, null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const c = canvasRef.current;
    if (c) clearCanvas(c.getContext("2d"));
    return () => stopAll();
  }, [stopAll]);

  const isRunning  = animStatus === "running";
  const isDone     = animStatus === "done";
  const canPlay    = !!selected && !loading;
  const eitherSeqActive = playAllActive || practiceAllActive
    || playAllRef.current.active || practiceSeqRef.current.active;

  // Play All / Practice All button label
  const seqBtnLabel = eitherSeqActive
    ? t.stop + (playAllProgress ? ` (${playAllProgress.idx + 1}/${playAllProgress.total})` : "")
    : playAllSaved && playModeTab === "play"
      ? t.resume + ` (${playAllSaved.idx + 1}/${playAllSaved.chars.length})`
      : playModeTab === "play" ? t.playAll : t.practiceAll;

  // ── Congrats: save / share ────────────────────────────────────────────────
  const handleSaveImage = () => {
    if (!compositeImageUrl) return;
    const a = document.createElement("a");
    a.href = compositeImageUrl;
    a.download = `hanapin_${activeFilters[0] || "japanese"}_practice.png`;
    a.click();
  };

  const handleShare = async () => {
    if (!compositeImageUrl) return;
    try {
      const blob = await (await fetch(compositeImageUrl)).blob();
      const file = new File([blob], "hanapin_practice.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: "Japanese Practice" });
      }
    } catch (_) {}
  };

  const congratsTypeName = activeFilters.includes("katakana") ? (t.katakana || "Katakana") : (t.hiragana || "Hiragana");
  const congratsCount    = Object.keys(completedChars).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="h-[100dvh] flex flex-col overflow-hidden bg-[#fffaf0]">

      {/* ── Toolbar ── */}
      <div className="flex-shrink-0 pt-[60px] z-30 bg-[#fffaf0]/95 backdrop-blur-md border-b border-[#fdcb6e]/20">
        <div className="max-w-3xl mx-auto flex items-center gap-2 px-4 py-2 flex-wrap">

          {/* Search */}
          <input
            type="text"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 px-3 text-xs border border-[#fdcb6e] rounded-lg w-36 bg-white focus:outline-none focus:ring-1 focus:ring-[#fdcb6e]"
          />

          {/* Script filter tabs */}
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => toggleFilter(f)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs border transition-all
                ${activeFilters.includes(f)
                  ? "bg-[#fdcb6e] text-black border-[#fdcb6e] font-semibold"
                  : "bg-white border-[#fdcb6e] text-gray-700 hover:bg-[#fdcb6e]/10"
                }`}
            >
              {f === "hiragana" ? t.hiragana : t.katakana}
            </button>
          ))}

          {/* Play All button */}
          <button
            onClick={() => {
              playTap();
              if (eitherSeqActive) { stopAll(); return; }
              handlePlayAll("play");
            }}
            disabled={!visibleChars.length && !playAllSaved && !eitherSeqActive}
            className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full border border-[#b5435f] transition-all disabled:opacity-40
              ${playAllActive
                ? "bg-[#b5435f] text-white"
                : "bg-white text-[#b5435f] hover:bg-[#b5435f]/5"
              }`}
          >
            {playAllActive ? (
              <><Square size={9} fill="currentColor" strokeWidth={0} /> {t.stop}</>
            ) : (
              <><Play size={9} fill="currentColor" strokeWidth={0} /> {t.playAll}</>
            )}
          </button>

          {/* Resume badge when saved */}
          {playAllSaved && playModeTab === "play" && !eitherSeqActive && (
            <button
              onClick={handlePlayAll}
              className="flex-shrink-0 h-8 px-3 rounded-full flex items-center gap-1.5 text-xs font-semibold border border-[#b5435f] bg-white text-[#b5435f] hover:bg-[#b5435f]/5"
            >
              <Play size={9} fill="currentColor" strokeWidth={0} />
              {t.resume} ({playAllSaved.idx + 1}/{playAllSaved.chars.length})
            </button>
          )}
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 min-h-0 max-w-3xl mx-auto w-full px-2 py-1 flex flex-col gap-1 overflow-hidden">

        {/* Character grid */}
        <div className="flex-shrink-0 overflow-hidden">
          {hiraganaAll.length > 0 && (
            <CharGrid allChars={hiraganaAll} activeChars={hiraganaActive} selected={selected} onSelect={handleSelect} completedChars={completedChars} practiceMode={practiceMode} practiceComplete={practiceComplete} />
          )}
          {katakanaAll.length > 0 && (
            <CharGrid allChars={katakanaAll} activeChars={katakanaActive} selected={selected} onSelect={handleSelect} completedChars={completedChars} practiceMode={practiceMode} practiceComplete={practiceComplete} />
          )}
          {visibleChars.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-2">{t.noMatch}</p>
          )}
        </div>

        {/* Canvas panel */}
        <div className="flex-1 min-h-0 bg-white rounded-2xl border border-[#fdcb6e]/40 shadow-sm overflow-hidden flex flex-col">

          {/* Header */}
          {selected && (
            <div className="flex-shrink-0 px-3 pt-1.5 pb-1.5 flex items-center gap-2 border-b border-[#fdcb6e]/20 flex-wrap">
              <span className="text-3xl font-bold text-gray-800 leading-none" style={{ fontFamily: "'M PLUS Rounded 1c', sans-serif" }}>{selected.char}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 leading-none">{selected.romaji}</p>
              </div>

              {/* Play / Replay + Practice Again toggle */}
              {isRunning || eitherSeqActive ? (
                <button disabled className="h-8 px-3 rounded-full flex items-center gap-1.5 bg-gradient-to-r from-[#ff6b6b] to-[#b5435f] text-white text-xs font-semibold opacity-60 cursor-not-allowed">
                  <Play size={10} fill="white" strokeWidth={0} /> {t.playing}
                </button>
              ) : currentStrokesRef.current.length > 0 ? (
                /* Segmented: Play Again | Practice Again */
                <div className="flex rounded-full border border-[#b5435f] overflow-hidden">
                  <button
                    onClick={() => { playTap(); clearPractice(); if (rafRef.current) cancelAnimationFrame(rafRef.current); animRef.current = null; loadAndPlayChar(selected, null); }}
                    disabled={!canPlay}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-white text-[#b5435f] hover:bg-[#b5435f]/5 disabled:opacity-40 transition-all"
                  >
                    <Play size={9} fill="currentColor" strokeWidth={0} /> {t.replay}
                  </button>
                  <button
                    onClick={() => { playTap(); enterPracticeMode(currentStrokesRef.current); }}
                    disabled={!canPlay || playAllActive}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border-l border-[#b5435f] bg-[#b5435f] text-white hover:bg-[#9e3a52] disabled:opacity-40 transition-all"
                  >
                    ✏ {t.practiceAgain}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handlePlayBtn}
                  disabled={!canPlay}
                  className="h-8 px-3 rounded-full flex items-center gap-1.5 bg-gradient-to-r from-[#ff6b6b] to-[#b5435f] text-white text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-md active:scale-95 transition-all"
                >
                  <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                    <Play size={10} fill="#ff6b6b" strokeWidth={0} />
                  </div>
                  {t.play}
                </button>
              )}

              {/* Speed slider */}
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-400 select-none">🐢</span>
                <input
                  type="range" min={0.25} max={3} step={0.05}
                  value={speedMult}
                  onChange={(e) => setSpeedMult(parseFloat(e.target.value))}
                  className="w-20 accent-[#b5435f] cursor-pointer"
                />
                <span className="text-[10px] text-gray-400 select-none">🐇</span>
                <span className="text-[10px] text-gray-500 w-7 text-right tabular-nums">{speedMult.toFixed(1)}×</span>
              </div>
            </div>
          )}

          {/* Practice stroke / win banner */}
          {selected && (practiceMode || congratsCount > 0) && (
            <div className="flex-shrink-0 px-4 py-1 flex items-center justify-center border-b border-[#fdcb6e]/20 bg-[#f0f8ff]">
              {practiceMode && !playAllActive ? (
                practiceComplete ? (
                  <span className="text-sm font-semibold text-[#b5435f]">{t.practiceWin}</span>
                ) : (
                  <span className="text-xs font-medium text-[#2797c4]">
                    {typeof t.practiceHint === "function" ? t.practiceHint(practiceStrokeIdx + 1) : t.practiceHint}
                  </span>
                )
              ) : congratsCount > 0 ? (
                <button
                  onClick={() => { playTap(); setShowCompositePopup(true); }}
                  className="text-sm font-semibold text-[#2797c4] hover:underline"
                >
                  {t.charactersDone(congratsCount)}
                </button>
              ) : null}
            </div>
          )}

          {/* Canvas */}
          <div className="flex-1 min-h-0 flex justify-center items-center bg-white">
            <div className="relative" style={{ aspectRatio: "1/1", maxHeight: "100%", maxWidth: "100%" }}>
              <canvas
                ref={canvasRef}
                width={CANVAS_SIZE} height={CANVAS_SIZE}
                className="w-full h-full block"
                onContextMenu={(e) => e.preventDefault()}
              />
              {/* Practice overlay canvas */}
              <canvas
                ref={practiceCanvasRef}
                width={CANVAS_SIZE} height={CANVAS_SIZE}
                className="absolute inset-0 w-full h-full"
                style={{
                  touchAction:   practiceMode ? "none" : "auto",
                  cursor:        practiceMode ? "crosshair" : "default",
                  pointerEvents: practiceMode ? "auto" : "none",
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onContextMenu={(e) => e.preventDefault()}
              />
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/85">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-[#fdcb6e] border-t-[#b5435f] rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs">{t.loading}</p>
                  </div>
                </div>
              )}
              {loadError && !loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/95 p-4">
                  <div className="text-center">
                    <p className="text-2xl mb-1">⚠️</p>
                    <p className="text-[#b5435f] text-xs leading-snug max-w-xs">{loadError}</p>
                  </div>
                </div>
              )}
              {!selected && !loading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-gray-200 text-8xl font-bold select-none leading-none">あ</p>
                  <p className="text-gray-300 text-xs mt-1">{t.selectChar}</p>
                </div>
              )}
            </div>
          </div>
        </div>


      {/* ── Hanamaru animation overlay ── */}
      {showHanamaru && (
        <div className="fixed inset-0 z-[60] pointer-events-none flex items-center justify-center">
          <HanamaruPlayer src={_hanamaruSrc} onComplete={() => setShowHanamaru(false)} />
        </div>
      )}

      {/* ── Composite popup modal ── */}
      {showCompositePopup && compositeImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => { setShowCompositePopup(false); setShowHanamaru(false); }}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl flex flex-col items-center gap-3 p-5"
            style={{ width: "min(92vw, 520px)" }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => { playTap(); setShowCompositePopup(false); setShowHanamaru(false); }}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X size={18} />
            </button>
            <p className="text-base font-bold text-[#b5435f] text-center pr-6">
              {t.congratsTitle(congratsTypeName, congratsCount)}
            </p>
            <p className="text-xs text-gray-500 text-center">{t.congratsSub}</p>
            <img
              src={compositeImageUrl}
              alt="Your practice"
              className="w-full rounded-xl border border-gray-100 shadow-sm object-contain"

            />
            <div className="flex gap-3">
              <button
                onClick={() => { playTap(); handleSaveImage(); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2797c4] text-white text-sm font-semibold hover:bg-[#1a7aab] active:scale-95 transition-all"
              >
                <Download size={14} /> {t.save}
              </button>
              <button
                onClick={() => { playTap(); handleShare(); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#2797c4] text-[#2797c4] text-sm font-semibold hover:bg-[#2797c4]/5 active:scale-95 transition-all"
              >
                <Share2 size={14} /> {t.share}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </section>
  );
}
